import * as Yup from 'yup';
import { AppRegistrationSection } from '../../AppRegistrationSection';

import { ButtonActor, InputActor, Spacer, Text, Title } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { mobilePhoneNumberValid } from '../../utils/regularExpressions';
import { phoneSanitizer } from '../../utils/sanitizers';
import { routes } from 'routes';
import { useCallback, useState } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';

import { usePhoneVerificationScreenLogic } from '../PhoneVerification/usePhoneVerificationScreenLogic';
import { useTranslation } from 'react-i18next';
import layout from 'scss/layout.module.scss';
import styles from './PhoneNumberChange.module.scss';

export default function PhoneVerificationChangePhone() {
  const i18Next = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setError } = useErrorManager();
  const { updateClientPhone } = usePhoneVerificationScreenLogic();
  const { client } = useClient();

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(i18Next.t('validation:required_field'))
      .test('wrong_format', i18Next.t('validation:wrong_format'), (value) =>
        mobilePhoneNumberValid(value ?? ''),
      ),
  });

  const onSubmit = useCallback(
    async (data: { phoneNumber: string }) => {
      formik.setStatus({ errors: {} });
      if (data.phoneNumber === client?.mobilePhone?.mobilePhone) {
        setError('same_phone');
      } else {
        try {
          setLoading(true);
          await updateClientPhone(data.phoneNumber);

          //allow to send sms with new phone number
          if (sessionStorage.getItem('lastSMSSent')) {
            localStorage.removeItem('lastSMSSent');
          }

          navigate(routes.FirstLoan_PhoneVerification);
        } catch (error) {
          const errors = error.fieldErrors || error.errors;
          if (
            errors?.[0]?.messageTemplate === 'already_exists_in_db' &&
            errors?.[0]?.property === 'mobilePhone'
          ) {
            setError('phone_already_exists');
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [updateClientPhone, navigate, setError],
  );

  const formik = useFormik({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema,
    onSubmit,
  });

  const content = getScreenTranslation('phoneChange', i18Next);

  return (
    <Frame {...frameConfig} sectionID={AppRegistrationSection.Identification}>
      <div className={layout['width-sm']}>
        <Title text={content.heading} />
        <Spacer marginTop="23px" marginBottom="15px">
          <Text
            text={content.subheading}
            level={2}
            kind="p"
            className={styles.subheading}
            dataTest="phoneNumberChange-subheading"
          />
        </Spacer>

        <form onSubmit={formik.handleSubmit}>
          <InputActor
            formik={formik}
            name="phoneNumber"
            label={content.mobile_phone}
            sanitizer={phoneSanitizer}
            analytics={{
              dontTrackContent: true,
            }}
            inputProps={{
              allowSpaces: false,
            }}
          />

          <ButtonActor
            buttonProps={{
              className: styles['continue-button'],
              dataTest: 'phoneNumberChange-continue-btn',
              type: 'submit',
              loading,
            }}
            formik={formik}
          >
            {content.continue_button}
          </ButtonActor>
        </form>
      </div>
    </Frame>
  );
}
